import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

export interface ThemeContextType {
  isDarkMode: boolean;
  theme: ThemeMode;
  toggleDarkMode: () => void;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Synchronous initialization from localStorage or system preference to avoid theme flickering
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    try {
      const savedDark = localStorage.getItem('darkMode');
      if (savedDark !== null) {
        return JSON.parse(savedDark) === true;
      }
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme === 'dark';
      }
      return typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch (e) {
      return false;
    }
  });

  // Apply theme changes to the DOM and persist
  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;

    if (isDarkMode) {
      htmlElement.classList.add('dark');
      htmlElement.setAttribute('data-theme', 'dark');
      htmlElement.setAttribute('data-bs-theme', 'dark');
      htmlElement.style.colorScheme = 'dark';
      if (bodyElement) {
        bodyElement.classList.add('dark');
        bodyElement.setAttribute('data-theme', 'dark');
        bodyElement.setAttribute('data-bs-theme', 'dark');
      }
    } else {
      htmlElement.classList.remove('dark');
      htmlElement.setAttribute('data-theme', 'light');
      htmlElement.setAttribute('data-bs-theme', 'light');
      htmlElement.style.colorScheme = 'light';
      if (bodyElement) {
        bodyElement.classList.remove('dark');
        bodyElement.setAttribute('data-theme', 'light');
        bodyElement.setAttribute('data-bs-theme', 'light');
      }
    }

    try {
      localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
      localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    } catch (e) {
      // Ignore localStorage quotas / private browsing errors
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => setIsDarkMode((prev) => !prev);
  const toggleTheme = () => setIsDarkMode((prev) => !prev);
  const setTheme = (mode: ThemeMode) => setIsDarkMode(mode === 'dark');

  const value: ThemeContextType = {
    isDarkMode,
    theme: isDarkMode ? 'dark' : 'light',
    toggleDarkMode,
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
